import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { colors } from "@/components/settings/theme";

// Toasts: short confirmations ("Link copied") and errors. They slide in at the
// bottom so they never cover a screen's back/close buttons or title, and they
// use the same dark surface as help tooltips. Auto-save does not use toasts: the
// screen header shows "Saving… / Saved" (components/settings/SaveStatus.js).
//
// Show toasts with notify.success/error/info. Don't mount extra hosts in
// screens: the library uses the last-mounted host, and a host in a hidden
// screen swallows the toast (that's how "Copied" went missing in the modal).

const ICONS = {
  success: { name: "checkmark-circle", color: "#4ADE80" },
  error: { name: "alert-circle", color: "#FCA5A5" },
  info: { name: "information-circle", color: "#93C5FD" },
};

const ToastCard = ({ type, text1, text2, onPress, hide }) => {
  const icon = ICONS[type] || ICONS.info;
  return (
    <Pressable
      onPress={() => {
        onPress?.();
        hide?.();
      }}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
      style={[
        tw`flex-row items-center px-4 py-3 mx-4 rounded-xl`,
        {
          backgroundColor: colors.tooltip,
          maxWidth: 440,
          minWidth: 220,
          shadowColor: "#000",
          shadowOpacity: 0.18,
          shadowRadius: 12,
          shadowOffset: { width: 0, height: 4 },
          elevation: 6,
        },
      ]}
    >
      <Ionicons
        name={icon.name}
        size={20}
        color={icon.color}
        style={tw`mr-2.5`}
      />
      <View style={tw`flex-shrink`}>
        {!!text1 && (
          <Text style={tw`text-[14px] leading-[19px] font-semibold text-white`}>
            {text1}
          </Text>
        )}
        {!!text2 && (
          <Text
            numberOfLines={3}
            style={tw`text-[13px] leading-[18px] text-[#D1D5DB] mt-0.5`}
          >
            {text2}
          </Text>
        )}
      </View>
    </Pressable>
  );
};

export const toastConfig = {
  success: (props) => <ToastCard {...props} type="success" />,
  error: (props) => <ToastCard {...props} type="error" />,
  info: (props) => <ToastCard {...props} type="info" />,
};

// One host for the whole app, mounted at the root (AppRouting), above every
// screen and the wide-layout event modal. Only RN <Modal>s that need toasts
// (they cover the root) mount their own.
const AppToast = (props) => {
  const insets = useSafeAreaInsets();
  return (
    <View
      pointerEvents="box-none"
      style={[StyleSheet.absoluteFill, { zIndex: 10000, elevation: 10000 }]}
    >
      <Toast
        config={toastConfig}
        position="bottom"
        bottomOffset={Math.max(insets.bottom, 8) + 16}
        visibilityTime={3000}
        {...props}
      />
    </View>
  );
};

const show = (type, text1, text2, options = {}) =>
  Toast.show({
    type,
    text1,
    text2,
    position: "bottom",
    visibilityTime: type === "error" ? 6000 : 3000,
    ...options,
  });

export const notify = {
  success: (text1, text2, options) => show("success", text1, text2, options),
  error: (text1, text2, options) => show("error", text1, text2, options),
  info: (text1, text2, options) => show("info", text1, text2, options),
  hide: () => Toast.hide(),
};

export default AppToast;
